/**
 * useUploadPhoto Hook
 * Custom hook for uploading a single profile photo
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useAuthStore } from "@/features/auth/store/authStore"
import apiClient from "@/lib/api-client"
import { getProfilePhotos } from "./useProfilePhotos"

async function uploadPhoto(userId: string, file: File, isCover?: boolean) {
  const formData = new FormData()
  formData.append("file", file)
  if (isCover !== undefined) {
    formData.append("isCover", String(isCover))
  }

  const { data } = await apiClient.post(`api/profiles/${userId}/photos`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  })

  return data.data.photo
}

export function useUploadPhoto() {
  const queryClient = useQueryClient()
  const setUser = useAuthStore(state => state.setUser)

  return useMutation({
    mutationFn: ({ userId, file, isCover }: { userId: string; file: File; isCover?: boolean }) =>
      uploadPhoto(userId, file, isCover),
    onSuccess: async (_newPhoto, variables) => {
      // Invalidate and refetch photos query
      await queryClient.invalidateQueries({ queryKey: ["photos", variables.userId] })

      // Fetch fresh photos from API and update auth store
      const freshPhotos = await getProfilePhotos(variables.userId)
      const user = useAuthStore.getState().user

      if (user) {
        const coverPhoto = freshPhotos.find((p: Record<string, unknown>) => p.isCover === true)
        setUser({
          ...user,
          profile_photos: freshPhotos as never[],
          profilePictureUrl: coverPhoto
            ? ((coverPhoto as Record<string, unknown>).filePath as string)
            : user.profilePictureUrl,
        })
      }
    },
  })
}
